import React, { useState } from "react";
import { Link } from "react-router-dom";
import { compressImage, PhotoPayload } from "./imageUtil";
import { submitNetlifyForm, base64ToBlob } from "./netlifyForms";
import { track } from "./analytics";
import "./BusinessQuotePage.css";

/** Free business quote — bulk e-waste, office cleanouts, secure data destruction. */

const SERVICES = [
  "💻 E-waste & electronics",
  "🪑 Office furniture",
  "🏢 Full office cleanout",
  "🔒 Secure data destruction",
  "👕 Textiles / clothing",
  "📦 Other",
];

const WHO_WE_SERVE = [
  { icon: "🏢", h: "Offices & Startups", p: "Upgrades, moves, and downsizing — we clear out old tech and furniture with minimal disruption to your team." },
  { icon: "🏬", h: "Property & Building Managers", p: "Move-outs, basement cleanouts, and recurring recycling days that keep your buildings clutter-free for tenants." },
  { icon: "🏫", h: "Schools & Universities", p: "E-waste and clothing drives, retired classroom tech — plus impact numbers you can share with your community." },
  { icon: "🍽️", h: "Restaurants & Retail", p: "Old equipment, fixtures, and surplus — picked up around your business hours, not ours." },
  { icon: "🤝", h: "Nonprofits & Shelters", p: "We collect what you can't use, and route refurbished electronics and essentials to the people you serve." },
  { icon: "🏛️", h: "Institutions & Agencies", p: "Planning a collection event or facility cleanout? We'll build a plan with the documentation your process requires." },
];

const HOW_IT_RUNS = [
  { h: "Tell us about the job", p: "Send the form below (photos help). You'll get a free, no-obligation quote — usually within 1–2 business days." },
  { h: "We collect on your schedule", p: "Our team shows up when it works for your operation, handles the heavy lifting, and leaves the space clean." },
  { h: "We sort & document", p: "Everything is sorted for reuse first, and data-bearing devices are tracked and handled with care." },
  { h: "Reuse first, always", p: "Working items are refurbished or donated; the rest is responsibly recycled — not landfilled." },
  { h: "You get the paperwork", p: "Receipts, donation acknowledgments where they apply, and an impact summary for your sustainability story." },
];

const BusinessQuotePage: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [services, setServices] = useState<string[]>([]);
  const [photos, setPhotos] = useState<{ payload: PhotoPayload; preview: string }[]>([]);

  const toggleService = (s: string) =>
    setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const onPickPhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const compressed = await Promise.all(
      files.slice(0, 3).map((f) => compressImage(f).catch(() => null))
    );
    setPhotos((cur) => [...cur, ...compressed.filter((c): c is NonNullable<typeof c> => !!c)].slice(0, 3));
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    const f = new FormData(e.currentTarget);
    const str = (k: string) => String(f.get(k) || "");
    const fields = {
      "bot-field": str("website"), // honeypot -> Netlify spam filter
      company: str("company"),
      name: str("name"),
      email: str("email"),
      phone: str("phone"),
      address: str("address"),
      services: services.map((s) => s.replace(/^\S+\s/, "")).join(", "),
      volume: str("volume"),
      timeline: str("timeline"),
      notes: str("notes"),
      report: f.get("report") === "yes" ? "yes" : "no",
    };
    try {
      // Captured by Netlify Forms (Site -> Forms -> "business-quote") and emailed to the team.
      await submitNetlifyForm(
        "business-quote",
        fields,
        photos.map((p, i) => ({
          field: `photo${i + 1}`,
          blob: base64ToBlob(p.payload.data, p.payload.type),
          filename: p.payload.name || `job-photo-${i + 1}.jpg`,
        }))
      );
      setSent(true);
      track("business_quote_submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError(true);
      track("form_error", { form: "business_quote" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bq-page">
      <div className="bq-wrap">
        {/* ── professional hero ── */}
        <div className="bq-hero">
          <span className="bq-eyebrow">🏢 Everything Eco for Business</span>
          <h1>Cleanouts &amp; recycling, handled like a real partner.</h1>
          <p className="bq-sub">
            Electronics recovery, office cleanouts, furniture and textile pickups for organizations
            across all five boroughs — reuse first, responsibly recycled, with the paperwork your
            organization needs.
          </p>
          <div className="bq-hero-btns">
            <a href="#quote" className="ee-btn ee-btn-green">Get a Free Quote →</a>
            <span className="bq-hero-note">Free quotes · No obligation · All 5 boroughs</span>
          </div>
        </div>

        {/* ── who we serve ── */}
        <section className="bq-section">
          <h2>Who we work with</h2>
          <div className="bq-who">
            {WHO_WE_SERVE.map((w) => (
              <div className="bq-who-card" key={w.h}>
                <span className="bq-who-icon">{w.icon}</span>
                <h3>{w.h}</h3>
                <p>{w.p}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── how a job runs ── */}
        <section className="bq-section">
          <h2>How a job runs</h2>
          <ol className="bq-steps">
            {HOW_IT_RUNS.map((s, i) => (
              <li key={s.h}>
                <span className="bq-step-n">{i + 1}</span>
                <div>
                  <strong>{s.h}</strong>
                  <p>{s.p}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ── founded by operators ── */}
        <section className="bq-operators">
          <div className="bq-op-badge">Founded by operators</div>
          <h2>We&rsquo;ve done this work from the inside.</h2>
          <p>
            Before Everything Eco, founders Julio Ramirez and Nick John spent years inside New York
            City electronics recycling operations — testing and refurbishing thousands of devices,
            coordinating commercial and residential collection routes, managing inventory, handling
            data-bearing equipment, and training teams. When we quote your job, it comes from
            experience, not a script.
          </p>
          <div className="bq-op-chips">
            <span>🔧 Refurbishment &amp; repair</span>
            <span>🚚 NYC collection logistics</span>
            <span>📦 Inventory &amp; asset workflows</span>
            <span>🔒 Data-aware handling</span>
            <span>🎓 Workforce training</span>
          </div>
          <p className="bq-op-line">Recovering valuable materials. Developing valuable people.</p>
        </section>

        {/* ── quote form ── */}
        <div className="bq-head" id="quote">
          <span className="bq-eyebrow">📋 Ready when you are</span>
          <h1>Get a free business quote</h1>
          <p className="bq-sub">
            Upgrading, moving, or downsizing? Tell us what you've got and we'll come back with a free,
            no-obligation quote — with the paperwork your business needs.
          </p>
        </div>

        <div className="bq-grid">
          <div className="bq-card">
            {sent ? (
              <div className="bq-thanks">
                <img
                  src={`${process.env.PUBLIC_URL}/earthjr-thumbs.png`}
                  alt="Earth Jr. celebrating"
                  style={{ width: 150, height: "auto" }}
                />
                <h2>Thank you — request received!</h2>
                <p>
                  Our team will review your details and get back to you with a free, no-obligation quote —
                  usually within 1–2 business days. Keep an eye on your inbox. 💚
                </p>
                <Link to="/" className="ee-btn ee-btn-green">← Back to Home</Link>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                  <label>Leave this empty: <input type="text" name="website" tabIndex={-1} autoComplete="off" /></label>
                </p>
                <div className="bq-row">
                  <div className="bq-field">
                    <label>Company name <span className="bq-req">*</span></label>
                    <input type="text" name="company" placeholder="Acme Co." required />
                  </div>
                  <div className="bq-field">
                    <label>Your name <span className="bq-req">*</span></label>
                    <input type="text" name="name" placeholder="Jordan Rivera" required />
                  </div>
                </div>

                <div className="bq-row">
                  <div className="bq-field">
                    <label>Work email <span className="bq-req">*</span></label>
                    <input type="email" name="email" placeholder="you@company.com" required />
                  </div>
                  <div className="bq-field">
                    <label>Phone</label>
                    <input type="tel" name="phone" placeholder="(917) 555-0100" />
                  </div>
                </div>

                <div className="bq-field">
                  <label>Pickup address / borough <span className="bq-req">*</span></label>
                  <input type="text" name="address" placeholder="123 Main St, Brooklyn, NY 11201" required />
                </div>

                <div className="bq-field">
                  <label>What do you need picked up? <span className="bq-req">*</span></label>
                  <div className="bq-checks">
                    {SERVICES.map((s) => (
                      <label key={s} className="bq-check">
                        <input
                          type="checkbox"
                          checked={services.includes(s)}
                          onChange={() => toggleService(s)}
                        />{" "}
                        {s}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bq-row">
                  <div className="bq-field">
                    <label>Approximate volume</label>
                    <select name="volume" defaultValue="">
                      <option value="">Select…</option>
                      <option>A few items</option>
                      <option>A room's worth</option>
                      <option>A full floor / office</option>
                      <option>Multiple floors / large move</option>
                      <option>Not sure yet</option>
                    </select>
                  </div>
                  <div className="bq-field">
                    <label>Ideal timeline</label>
                    <select name="timeline" defaultValue="">
                      <option value="">Select…</option>
                      <option>ASAP (this week)</option>
                      <option>Within 2 weeks</option>
                      <option>This month</option>
                      <option>Just planning ahead</option>
                    </select>
                  </div>
                </div>

                <div className="bq-field">
                  <label>Photos of the job <span style={{ fontWeight: 500, color: "var(--text-faint)" }}>(optional, up to 3 — helps us quote faster)</span></label>
                  <input type="file" accept="image/*" multiple onChange={onPickPhotos} />
                  {photos.length > 0 && (
                    <div className="bq-photos">
                      {photos.map((p, i) => (
                        <div className="bq-photo" key={i}>
                          <img src={p.preview} alt={`Attached job item ${i + 1}`} />
                          <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label="Remove photo">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bq-field">
                  <label>Anything else we should know?</label>
                  <textarea
                    name="notes"
                    placeholder="Elevator or stairs, number of devices, ESG/impact report needed, preferred dates…"
                  />
                </div>

                <label className="bq-check bq-report">
                  <input type="checkbox" name="report" value="yes" /> 📊 I'd like an ESG / CSR impact report with my cleanout
                </label>

                {error && (
                  <p className="bq-error">
                    Hmm, that didn't send. Please try again — or email us directly at{" "}
                    <a href="mailto:everythingeconyc@gmail.com">everythingeconyc@gmail.com</a>.
                  </p>
                )}

                <button className="ee-btn ee-btn-green bq-submit" type="submit" disabled={sending}>
                  {sending ? "Sending…" : "Request My Free Quote →"}
                </button>
              </form>
            )}
          </div>

          <div className="bq-side">
            <div className="bq-card bq-why">
              <h3>Why businesses choose us</h3>
              <div className="bq-item"><span>🔒</span><div><strong>Secure data destruction.</strong> We wipe or physically destroy drives — your data never leaves with your old tech.</div></div>
              <div className="bq-item"><span>📊</span><div><strong>ESG / CSR reports.</strong> A clear record of waste diverted, perfect for your sustainability story.</div></div>
              <div className="bq-item"><span>🧾</span><div><strong>Tax-deductible receipts.</strong> Qualifying donations come with paperwork through our nonprofit partners.</div></div>
              <div className="bq-item"><span>♻️</span><div><strong>Responsible recycling.</strong> Refurbished, donated, or recycled — never landfilled.</div></div>
            </div>
            <div className="bq-card bq-pills">
              <span className="bq-pill">✅ Free quotes</span>
              <span className="bq-pill">📅 Flexible scheduling</span>
              <span className="bq-pill">🗽 All 5 boroughs</span>
              <span className="bq-pill">🔒 Secure data handling</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessQuotePage;
